import React, { useCallback, useRef, useState } from "react";
import { apiFetch, downloadBlob } from "../../api/fddClient";

/**
 * Return Reconstruction (v2) — inventory, review, then run.
 *
 * Upload everything the client sent. The inventory says what is actually in
 * there: which federal form, in which file, on which page, for which tax year.
 * You review that list and pick what to run.
 *
 * NOBODY TYPES A TAX YEAR. Every one of these forms prints the year in its
 * header, so it is read off the page. A typed year is a claim about the
 * document, and the two can disagree: the 1120-S inserted a line 19 in 2023,
 * so a FY2022 return read against the 2023 box list gives the model a line
 * number pointing at one box and a caption pointing at another.
 *
 * Deliberately alongside the v1 Deal Book Builder rather than replacing it.
 * This path has not been used on an engagement yet; the old one has.
 */

type Phase = "idle" | "inventorying" | "reviewing" | "running" | "done" | "error";

interface InventoryItem {
  index: number;
  file: string;
  form: string;
  title: string;
  page: number;
  taxYear: number | null;
  runnable: boolean;
  note: string;
}

interface JobStatus {
  status: "running" | "done" | "error";
  progress?: string;
  error?: string;
  warnings?: string[];
  summary?: {
    formId?: string;
    taxYear?: number;
    formPage1?: number;
    pages?: number;
    boxesRead?: number;
    boxesFilled?: number;
    writtenToForm?: number;
    /**
     * Set on the phase jobs rather than a transcription job.
     *
     * Phase 1 reports the materiality bases; phase 2 reports the questions.
     * Both are read through their own interface at the point of use, because a
     * union here would make every field optional at every call site.
     */
    years?: unknown[];
    dealBook?: DealBookReport;
    suggestedPercents?: number[];
  };
}

/** One item being reconstructed, with whatever its job has reported so far. */
interface RunRow {
  index: number;
  jobId: string;
  form: string;
  taxYear: number;
  file: string;
  status: "running" | "done" | "error";
  progress?: string;
  error?: string;
  warnings?: string[];
  summary?: JobStatus["summary"];
}

interface BoxValue {
  id: string;
  value: string;
  page: number | null;
  lineRef: string;
  label: string;
}

/** A materiality base, with the amount READ OFF the transcribed return. */
interface MaterialityBase {
  id: string;
  label: string;
  note: string;
  /** Null when that line is blank on the return. */
  amount: number | null;
  source: string | null;
  recommended: boolean;
}

/** What one written sheet contains. */
interface SheetReport {
  sheet: string;
  formId: string;
  years: number[];
  rows: number;
  written: number;
  blank: number;
  /** Cells the form did not have that year — the only honest "n/a". */
  notOnForm: number;
  /** Transcribed boxes with no row. Should be empty. */
  orphaned: string[];
}

/** An engagement fact the returns could not supply. */
interface EngagementField {
  id: string;
  section: string;
  label: string;
  kind: "text" | "number" | "percent" | "money" | "year" | "choice";
  choices?: string[];
  value: string;
  provenance: "rulebook" | "return" | "reviewer";
  why: string;
  /** What changes if this number moves. Shown on hover. */
  impact: string;
  /** Rules that cannot be evaluated until this is known. */
  unlocks: string[];
  required: boolean;
}

interface DealBookReport {
  downloadName: string;
  written: number;
  /** Which column holds which tax year, left to right. */
  yearColumns: Array<{ column: string; taxYear: number }>;
  sheets: SheetReport[];
}

/** Phase 1 — what the deal book run reports back, per tax year. */
interface BasesForYear {
  taxYear: number;
  form: string;
  entityName: string;
  bases: MaterialityBase[];
}

interface BuildSummary {
  years: BasesForYear[];
  suggestedPercents: number[];
  dealBook: DealBookReport;
  engagement: EngagementField[];
}

/** One question that was not asked, and who can unblock it. */
interface CannotAsk {
  questionId: string;
  ruleId: string;
  kind: "cleared" | "needs_document" | "needs_input" | "not_built";
  reason: string;
  owner: "nobody" | "the client" | "the deal team" | "us";
  needs: string[];
}

interface CannotAskGroup {
  kind: CannotAsk["kind"];
  label: string;
  owner: CannotAsk["owner"];
  items: CannotAsk[];
}

/** One rule that produced no question, and why. */
interface NotAsked {
  ruleId: string;
  name: string;
  outcome: "cleared" | "blocked_data" | "not_implemented";
  reason: string;
  needs: string[];
}

/** Phase 2 — the questions, per tax year. */
interface QuestionYear {
  taxYear: number;
  form: string;
  entityName: string;
  formsRead: string[];
  questions: number;
  materiality: number;
  materialityExplanation: string;
  counts: { ask: number; cleared: number; blocked_data: number; not_implemented: number };
  asked: Array<{
    id: string;
    ruleId: string;
    text: string;
    observedFact: string;
    priority: string;
    /** Set when asked on the rule book's standing instruction, not on a check. */
    askedByDefault: string | null;
  }>;
  notAsked: NotAsked[];
  cannotAsk: CannotAskGroup[];
}

/**
 * Every job is polled in ONE request per tick (the batch status route), and
 * the tick is 3s: the server limits each user to 120 requests a minute, and a
 * package of ten forms polled one job at a time used to blow straight past it.
 */
const POLL_MS = 3000;

/** Polls are background calls: never open a sign-in popup for one. */
const poll = (path: string) => apiFetch(path, {}, { interactive: false });

/** The filename the server chose, from Content-Disposition, or the fallback. */
function filenameFrom(res: Response, fallback: string): string {
  const header = res.headers.get("Content-Disposition") ?? "";
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : fallback;
}

const card: React.CSSProperties = {
  border: "1px solid var(--theme-border-strong)",
  borderRadius: 12,
  background: "#ffffff",
  padding: 16,
};
const sectionTitle: React.CSSProperties = {
  fontWeight: 700,
  color: "var(--theme-ink)",
  fontSize: 13,
  marginBottom: 10,
};
const primary: React.CSSProperties = {
  background: "var(--theme-accent)",
  color: "#fff",
  border: "1px solid var(--theme-accent)",
  borderRadius: 8,
  padding: "10px 16px",
  cursor: "pointer",
  fontWeight: 600,
};
/** Whole dollars — these are tax return figures, not cents. */
const money = (n: number): string => `$${Math.round(n).toLocaleString("en-US")}`;

const hint: React.CSSProperties = { marginTop: 8, fontSize: 12, color: "#6b7280" };

const ReconstructPanel: React.FC<{ projectId: string; projectName?: string }> = ({ projectId }) => {
  const pid = encodeURIComponent(projectId);
  const [files, setFiles] = useState<File[]>([]);
  const [inventoryId, setInventoryId] = useState<string | null>(null);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [scanned, setScanned] = useState<Array<{ file: string; pages: number }>>([]);
  const [chosen, setChosen] = useState<Set<number>>(new Set());
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const [values, setValues] = useState<BoxValue[]>([]);
  const [message, setMessage] = useState("");
  const [onlyFilled, setOnlyFilled] = useState(true);

  /** Set when the panel is torn down mid-poll, so the loops stop. */
  const cancelled = useRef(false);

  // Materiality, chosen AFTER the returns are read. The bases carry the
  // amounts off this return, so a reviewer can see what each one would give.
  const [matMode, setMatMode] = useState<"calculated" | "manual">("calculated");
  const [baseId, setBaseId] = useState<string>("");
  const [percent, setPercent] = useState<number>(3);
  const [manualAmount, setManualAmount] = useState<string>("");

  // Phase 1 — writes the one deal book and reads the bases.
  const [buildJobId, setBuildJobId] = useState<string | null>(null);
  const [buildStatus, setBuildStatus] = useState<JobStatus | null>(null);
  // Phase 2 — the questions.
  const [qJobId, setQJobId] = useState<string | null>(null);
  const [qStatus, setQStatus] = useState<JobStatus | null>(null);
  const [qBusy, setQBusy] = useState(false);

  const build = buildStatus?.summary as BuildSummary | undefined;
  const percents = build?.suggestedPercents?.length ? build.suggestedPercents : [1, 2, 3, 5];

  // The newest year's bases drive the choice: materiality is set against
  // current scale, not the oldest return in the pile.
  const basesForChoice = build?.years?.[0]?.bases ?? [];

  // Default to whichever base the server marked as the suggestion, once the
  // bases exist. Picked here rather than server-side so a reviewer's own choice
  // is never overwritten by a later poll.
  React.useEffect(() => {
    if (baseId || basesForChoice.length === 0) return;
    const suggested = basesForChoice.find((b) => b.recommended && b.amount);
    setBaseId(suggested?.id ?? basesForChoice.find((b) => b.amount)?.id ?? "");
  }, [basesForChoice, baseId]);

  // Seeded from the server once, then owned by the reviewer — a later poll
  // must not overwrite something they have typed.
  const [engagement, setEngagement] = useState<Record<string, string>>({});
  const [engagementSeeded, setEngagementSeeded] = useState(false);
  const engagementFields = build?.engagement ?? [];

  React.useEffect(() => {
    if (engagementSeeded || engagementFields.length === 0) return;
    setEngagement(Object.fromEntries(engagementFields.map((f) => [f.id, f.value])));
    setEngagementSeeded(true);
  }, [engagementFields, engagementSeeded]);

  const engagementReady = engagementFields.every(
    (f) => !f.required || (engagement[f.id] ?? "").trim() !== "",
  );

  // The forms-by-year grid. Ordered by whether we can read the form, then by
  // name, so the ones that matter sit at the top.
  const matrixForms = React.useMemo(() => {
    const runnableForms = new Set(items.filter((i) => i.runnable).map((i) => i.form));
    return [...new Set(items.map((i) => i.form))].sort((a, b) => {
      const ra = runnableForms.has(a) ? 0 : 1;
      const rb = runnableForms.has(b) ? 0 : 1;
      return ra - rb || a.localeCompare(b);
    });
  }, [items]);

  const matrixYears = React.useMemo(() => {
    const ys = [...new Set(items.map((i) => i.taxYear))];
    const known = ys.filter((y): y is number => y !== null).sort((a, b) => b - a);
    return ys.includes(null) ? [...known, null] : known;
  }, [items]);

  // The list defaults to forms we can actually read. Showing every supporting
  // schedule by default made the review look like a wall of things that had
  // gone wrong, when most of them feed a line the return already carries.
  const [showAllItems, setShowAllItems] = useState(false);
  const listedItems = showAllItems ? items : items.filter((i) => i.runnable);

  const chosenBase = basesForChoice.find((b) => b.id === baseId) ?? null;
  const manualParsed = Number(manualAmount.replace(/[,\s$]/g, ""));
  const computed =
    matMode === "manual"
      ? Number.isFinite(manualParsed) ? manualParsed : 0
      : chosenBase?.amount
        ? chosenBase.amount * (percent / 100)
        : 0;
  const materialityReady = computed > 0;

  /** Step 1 — upload everything and see what is in it. */
  const takeInventory = useCallback(async () => {
    if (files.length === 0) return;
    setPhase("inventorying");
    setMessage("");
    setItems([]);
    setChosen(new Set());
    setValues([]);

    try {
      const body = new FormData();
      body.append("projectId", projectId);
      for (const f of files) body.append("files", f);

      const res = await apiFetch("/api/tax/v2/inventory", { method: "POST", body });
      const started = await res.json();
      if (!res.ok) throw new Error(started.error || `Inventory failed (HTTP ${res.status})`);

      // The scan runs on the server in the background; poll until it is done.
      let payload: { status: string; error?: string; inventoryId: string; items?: InventoryItem[]; files?: Array<{ file: string; pages: number }> };
      for (;;) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const sr = await poll(`/api/tax/v2/inventory/${started.inventoryId}?projectId=${pid}`);
        payload = await sr.json();
        if (!sr.ok) throw new Error(payload.error || `Inventory failed (HTTP ${sr.status})`);
        if (payload.status === "error") throw new Error(payload.error || "Inventory failed");
        if (payload.status === "ready") break;
      }

      setInventoryId(payload.inventoryId);
      setItems(payload.items ?? []);
      setScanned(payload.files ?? []);
      // Preselect everything runnable: a package holds a return and its K-1s,
      // and wanting all of them is the common case rather than the exception.
      setChosen(new Set((payload.items ?? []).filter((i) => i.runnable).map((i) => i.index)));
      setRuns([]);
      setPhase("reviewing");
    } catch (err) {
      setPhase("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }, [files, projectId, pid]);

  /** Step 2 — run everything that was reviewed and ticked. */
  const run = useCallback(async () => {
    if (!inventoryId || chosen.size === 0) return;
    cancelled.current = false;
    setPhase("running");
    setMessage("");
    setValues([]);

    try {
      const res = await apiFetch(`/api/tax/v2/inventory/${inventoryId}/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, indices: [...chosen].sort((a, b) => a - b) }),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || `Could not start (HTTP ${res.status})`);

      setBuildJobId(payload.buildJobId ?? null);
      setBuildStatus(null);

      const started: RunRow[] = (payload.jobs ?? []).map(
        (j: { index: number; jobId: string; form: string; taxYear: number; file: string }) => ({
          ...j,
          status: "running" as const,
          progress: "Queued…",
        }),
      );
      setRuns(started);

      // Poll every job until all have settled -- all of them in one request per
      // tick. The server runs them together and throttles the vision calls
      // underneath, so they advance at once rather than one form at a time.
      const pending = new Set(started.map((r) => r.jobId));
      while (!cancelled.current && pending.size > 0) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const jr = await poll(`/api/tax/v2/reconstruct/jobs?projectId=${pid}&ids=${[...pending].join(",")}`);
        if (!jr.ok) continue;
        const { jobs } = (await jr.json()) as { jobs: Record<string, JobStatus | null> };
        for (const [id, st] of Object.entries(jobs)) {
          // A null means the job is gone for good — jobs live in the server's
          // memory, so a restart takes them with it. Said out loud, because the
          // row otherwise sits on its last message forever and reads as work
          // still in progress.
          if (!st) {
            setRuns((prev) =>
              prev.map((r) =>
                r.jobId === id
                  ? { ...r, status: "error", error: "The server restarted, so this run was lost — upload again and re-run." }
                  : r,
              ),
            );
            pending.delete(id);
            continue;
          }
          setRuns((prev) =>
            prev.map((r) =>
              r.jobId === id
                ? { ...r, status: st.status, progress: st.progress, error: st.error, warnings: st.warnings, summary: st.summary }
                : r,
            ),
          );
          if (st.status !== "running") pending.delete(id);
        }
      }

      // Phase 1 waits on the transcriptions, so it starts as the last of them
      // finishes. It writes the one deal book and reads the materiality
      // bases off the returns; polled here so both appear without anything
      // further being pressed.
      const bid: string | null = payload.buildJobId ?? null;
      if (bid) {
        for (;;) {
          if (cancelled.current) break;
          await new Promise((r) => setTimeout(r, POLL_MS));
          const ar = await poll(`/api/tax/v2/reconstruct/jobs/${bid}?projectId=${pid}`);
          if (!ar.ok) break;
          const st: JobStatus = await ar.json();
          setBuildStatus(st);
          if (st.status !== "running") break;
        }
      }

      setPhase("done");
    } catch (err) {
      setPhase("error");
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }, [inventoryId, chosen, projectId, pid]);

  /**
   * Phase 2 — generate the questions, now that materiality is chosen.
   *
   * Nothing is re-read: the server held what it transcribed, so the questions
   * come off the same box values that went into the deal book.
   */
  const generateQuestions = useCallback(async () => {
    if (!inventoryId || !materialityReady) return;
    setQBusy(true);
    setQStatus(null);
    setMessage("");
    try {
      const res = await apiFetch(`/api/tax/v2/inventory/${inventoryId}/questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          materiality:
            matMode === "manual"
              ? { mode: "manual", amount: manualParsed }
              : { mode: "calculated", base: baseId, percent },
          engagement,
        }),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || `Could not start (HTTP ${res.status})`);
      setQJobId(payload.jobId);

      for (;;) {
        if (cancelled.current) break;
        await new Promise((r) => setTimeout(r, POLL_MS));
        const jr = await poll(`/api/tax/v2/reconstruct/jobs/${payload.jobId}?projectId=${pid}`);
        if (!jr.ok) break;
        const st: JobStatus = await jr.json();
        setQStatus(st);
        if (st.status !== "running") break;
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setQBusy(false);
    }
  }, [inventoryId, materialityReady, matMode, manualParsed, baseId, percent, engagement, projectId, pid]);

  /** Show the values of one finished run, on demand. */
  const showValues = useCallback(async (jobId: string) => {
    const res = await apiFetch(`/api/tax/v2/reconstruct/jobs/${jobId}/values?projectId=${pid}`);
    if (res.ok) setValues((await res.json()).boxes ?? []);
  }, [pid]);

  /**
   * Download a finished job's file. Fetched with the signed-in token rather than
   * linked: a plain link cannot carry it, and the server refuses the request.
   */
  const downloadJobFile = useCallback(async (jobId: string, fallbackName: string) => {
    try {
      const res = await apiFetch(`/api/tax/v2/reconstruct/jobs/${jobId}/file?projectId=${pid}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Download failed (HTTP ${res.status})`);
      }
      downloadBlob(await res.blob(), filenameFrom(res, fallbackName));
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }, [pid]);

  const runnable = items.filter((i) => i.runnable);
  const shown = onlyFilled ? values.filter((v) => v.value !== "") : values;
  const busy = phase === "inventorying" || phase === "running";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={card}>
        <div style={sectionTitle}>1. Upload everything you were given</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="file"
            accept="application/pdf"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            style={{ color: "#1f2937" }}
          />
          <button
            style={{ ...primary, opacity: files.length === 0 || busy ? 0.55 : 1 }}
            disabled={files.length === 0 || busy}
            onClick={takeInventory}
          >
            {phase === "inventorying" ? "Reading…" : "Take inventory"}
          </button>
          {files.length > 0 && (
            <span style={{ fontSize: 12, color: "var(--theme-ink-soft)" }}>
              {files.length} file{files.length === 1 ? "" : "s"} selected
            </span>
          )}
        </div>
        <div style={hint}>
          Whole packages are fine — state returns, elections and depreciation schedules and all.
          The forms and their tax years are read off the pages, so nothing needs selecting first.
        </div>
      </div>

      {phase === "error" && runs.length === 0 && (
        <div style={{ ...card, borderColor: "#f0b4ae" }}>
          <div style={{ color: "#b3261e" }}>{message}</div>
        </div>
      )}

      {items.length > 0 && (
        <div style={{ ...card, marginBottom: 12 }}>
          <div style={sectionTitle}>What we found, by year</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
            Every form in the upload, against every year it was found for. Green is a form we can
            read; grey means it is supporting detail that feeds a line the return already carries.
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--theme-tint-7)", color: "var(--theme-ink)" }}>
                  <Th>Form</Th>
                  {matrixYears.map((y) => (
                    <Th key={String(y)}>{y === null ? "year not read" : `FY${String(y).slice(2)}`}</Th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrixForms.map((form) => {
                  const anyRunnable = items.some((i) => i.form === form && i.runnable);
                  return (
                    <tr key={form} style={{ borderTop: "1px solid var(--theme-tint-13)" }}>
                      <Td>
                        <span style={{ fontWeight: anyRunnable ? 600 : 400, color: anyRunnable ? "var(--theme-ink)" : "#6b7280" }}>
                          {form}
                        </span>
                      </Td>
                      {matrixYears.map((y) => {
                        const found = items.filter((i) => i.form === form && i.taxYear === y);
                        if (found.length === 0) {
                          return <Td key={String(y)}><span style={{ color: "var(--theme-tint-25)" }}>·</span></Td>;
                        }
                        const ready = found.some((i) => i.runnable);
                        return (
                          <Td key={String(y)}>
                            <span
                              title={found.map((i) => `${i.file} p${i.page}`).join("\n")}
                              style={{
                                display: "inline-block", minWidth: 22, textAlign: "center",
                                fontWeight: 700, fontSize: 11, padding: "2px 7px", borderRadius: 6,
                                color: ready ? "var(--theme-ink)" : "#6b7280",
                                background: ready ? "var(--theme-tint-15)" : "#f3f4f6",
                                border: `1px solid ${ready ? "var(--theme-border-strong)" : "#e5e7eb"}`,
                              }}
                            >
                              {found.length}
                            </span>
                          </Td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={hint}>
            {runnable.length} of {items.length} rows can be read.{" "}
            {scanned.length > 0 && (
              <>
                Scanned {scanned.length} file{scanned.length === 1 ? "" : "s"},{" "}
                {scanned.reduce((n, f) => n + f.pages, 0)} pages.
              </>
            )}
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div style={card}>
          <div style={{ ...sectionTitle, display: "flex", alignItems: "baseline", gap: 12 }}>
            <span>
              2. What we can read — {runnable.length} form{runnable.length === 1 ? "" : "s"}
            </span>
            <label style={{ fontSize: 11.5, fontWeight: 400, color: "#6b7280", display: "flex", gap: 5, alignItems: "center" }}>
              <input
                type="checkbox"
                checked={showAllItems}
                onChange={(e) => setShowAllItems(e.target.checked)}
              />
              also show the {items.length - runnable.length} supporting page(s) we do not read
            </label>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--theme-tint-7)", color: "var(--theme-ink)", textAlign: "left" }}>
                  <Th>
                    <input
                      type="checkbox"
                      title="Select every form that can be run"
                      checked={runnable.length > 0 && chosen.size === runnable.length}
                      disabled={runnable.length === 0 || busy}
                      onChange={(e) =>
                        setChosen(e.target.checked ? new Set(runnable.map((i) => i.index)) : new Set())
                      }
                    />
                  </Th>
                  <Th>Form</Th>
                  <Th>Tax year</Th>
                  <Th>File</Th>
                  <Th>Page</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {listedItems.map((i) => (
                  <tr
                    key={i.index}
                    style={{
                      borderBottom: "1px solid var(--theme-tint-14)",
                      background: chosen.has(i.index) ? "var(--theme-tint-7)" : undefined,
                      opacity: i.runnable ? 1 : 0.6,
                    }}
                  >
                    <Td>
                      <input
                        type="checkbox"
                        checked={chosen.has(i.index)}
                        disabled={!i.runnable || busy}
                        onChange={(e) =>
                          setChosen((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(i.index);
                            else next.delete(i.index);
                            return next;
                          })
                        }
                      />
                    </Td>
                    <Td>
                      <strong>{i.form}</strong>
                      <div style={{ color: "#6b7280", fontSize: 11 }}>{i.title}</div>
                    </Td>
                    <Td>{i.taxYear ?? "—"}</Td>
                    <Td>{i.file}</Td>
                    <Td>{i.page}</Td>
                    <Td>
                      {i.runnable ? (
                        <span style={{ color: "var(--theme-ink)", fontWeight: 600 }}>ready</span>
                      ) : (
                        <span style={{ color: "#8a5a00" }}>{i.note}</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              style={{ ...primary, opacity: chosen.size === 0 || busy ? 0.55 : 1 }}
              disabled={chosen.size === 0 || busy}
              onClick={run}
            >
              {phase === "running"
                ? "Reading…"
                : `Read ${chosen.size} form${chosen.size === 1 ? "" : "s"} and build the deal book`}
            </button>
          </div>
          <div style={hint}>
            {scanned.length > 0 && (
              <>
                Scanned {scanned.length} file{scanned.length === 1 ? "" : "s"},{" "}
                {scanned.reduce((n, f) => n + f.pages, 0)} pages.{" "}
              </>
            )}
            Rows without a tax year are usually a cover letter or index page that mentions a form
            by name rather than containing one.
          </div>
        </div>
      )}

      {/* ── the deal book: one workbook, every form, every year ────── */}
      {buildJobId && (
        <div style={{ ...card, marginTop: 12 }}>
          <div style={sectionTitle}>
            Deal book
            {buildStatus?.status === "running" && " — building"}
            {buildStatus?.status === "error" && " — failed"}
          </div>

          {buildStatus?.status === "running" && (
            <div style={{ fontSize: 12.5, color: "#6b7280" }}>
              {buildStatus.progress ?? "Queued behind the forms being read…"}
            </div>
          )}
          {buildStatus?.status === "error" && (
            <div style={{ fontSize: 12.5, color: "#a33" }}>{buildStatus.error}</div>
          )}

          {build?.dealBook && buildStatus?.status === "done" && (
            <div style={{
              display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
              background: "var(--theme-tint-15)", border: "1px solid var(--theme-accent)", borderRadius: 8, padding: "10px 12px",
            }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 600, color: "var(--theme-ink)", fontSize: 13 }}>
                  {build.dealBook.downloadName}
                </div>
                <div style={{ fontSize: 12, color: "var(--theme-ink-soft)", marginTop: 2 }}>
                  {build.dealBook.written.toLocaleString("en-US")} figures across{" "}
                  {build.dealBook.sheets.length} sheet
                  {build.dealBook.sheets.length === 1 ? "" : "s"} — one workbook for the whole run.
                  {build.dealBook.yearColumns.length > 0 && (
                    <>
                      {" "}
                      Columns:{" "}
                      {build.dealBook.yearColumns
                        .map((c) => `${c.column} = FY${String(c.taxYear).slice(2)}`)
                        .join(", ")}
                      .
                    </>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--theme-ink-soft)", textAlign: "right", maxWidth: 190 }}>
                Downloadable once the questions are generated — one workbook holds the figures and
                the questions together.
              </div>
            </div>
          )}

          {/* Per sheet, so a shortfall is a number rather than an absence. */}
          {(build?.dealBook?.sheets ?? []).length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 10, fontSize: 12 }}>
              <thead>
                <tr style={{ color: "#6b7280", textAlign: "left" }}>
                  <th style={{ padding: "4px 8px", fontWeight: 600 }}>Sheet</th>
                  <th style={{ padding: "4px 8px", fontWeight: 600 }}>Years</th>
                  <th style={{ padding: "4px 8px", fontWeight: 600, textAlign: "right" }}>Rows</th>
                  <th style={{ padding: "4px 8px", fontWeight: 600, textAlign: "right" }}>Filled</th>
                  <th style={{ padding: "4px 8px", fontWeight: 600, textAlign: "right" }}>Blank on return</th>
                  <th style={{ padding: "4px 8px", fontWeight: 600, textAlign: "right" }}>Not on form</th>
                </tr>
              </thead>
              <tbody>
                {build!.dealBook.sheets.map((sh) => (
                  <tr key={sh.sheet} style={{ borderTop: "1px solid var(--theme-tint-13)" }}>
                    <td style={{ padding: "5px 8px" }}>{sh.sheet}</td>
                    <td style={{ padding: "5px 8px", color: "#6b7280" }}>
                      {sh.years.map((y) => `FY${String(y).slice(2)}`).join(", ")}
                    </td>
                    <td style={{ padding: "5px 8px", textAlign: "right" }}>{sh.rows}</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", color: "var(--theme-ink)", fontWeight: 600 }}>
                      {sh.written}
                    </td>
                    <td style={{ padding: "5px 8px", textAlign: "right", color: "#6b7280" }}>{sh.blank}</td>
                    <td style={{ padding: "5px 8px", textAlign: "right", color: "#6b7280" }}>{sh.notOnForm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {buildStatus?.warnings?.length ? (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12, color: "#8a5a00" }}>
              {buildStatus.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          ) : null}
        </div>
      )}

      {/* ── materiality, now that the figures are known ─────────────── */}
      {basesForChoice.length > 0 && (
        <div style={{ ...card, marginTop: 12 }}>
          <div style={sectionTitle}>Materiality</div>
          <div
            title="Raise materiality and fewer lines are large enough to ask about. Lower it and more are. Most of the rules compare against this figure."
            style={{ fontSize: 12, color: "#6b7280", marginBottom: 8, cursor: "help" }}
          >
            Amounts below were read off the returns just now, so you can see what each base gives
            before choosing. Most of the rules test against this, so the questions come next.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {basesForChoice.map((b) => {
              const usable = b.amount !== null && b.amount > 0;
              const selected = matMode === "calculated" && baseId === b.id;
              return (
                <label
                  key={b.id}
                  title={b.note}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, fontSize: 12.5,
                    padding: "7px 10px", borderRadius: 8,
                    background: selected ? "var(--theme-tint-15)" : "#fff",
                    border: `1px solid ${selected ? "var(--theme-accent)" : "var(--theme-tint-13)"}`,
                    cursor: usable ? "pointer" : "not-allowed",
                    opacity: usable ? 1 : 0.55,
                  }}
                >
                  <input
                    type="radio"
                    name="v2matbase"
                    disabled={!usable}
                    checked={selected}
                    onChange={() => { setMatMode("calculated"); setBaseId(b.id); }}
                  />
                  <span style={{ flex: 1, color: "#333" }}>
                    {b.label}
                    {b.recommended && usable && (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: "var(--theme-ink)",
                                     background: "var(--theme-tint-15)", padding: "1px 6px", borderRadius: 999 }}>
                        SUGGESTED
                      </span>
                    )}
                  </span>
                  <span style={{ fontFamily: "monospace", fontSize: 12.5, minWidth: 118,
                                 textAlign: "right", color: usable ? "#333" : "#8a5a00" }}>
                    {usable ? money(b.amount as number) : "not reported"}
                  </span>
                  <span style={{ fontFamily: "monospace", fontSize: 12, minWidth: 116,
                                 textAlign: "right", color: "#6b7280" }}>
                    {usable ? `${percent}% = ${money((b.amount as number) * (percent / 100))}` : ""}
                  </span>
                </label>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <span style={{ fontSize: 12.5, color: "var(--theme-ink)", fontWeight: 600 }}>Percentage</span>
            {percents.map((pc) => {
              const on = matMode === "calculated" && percent === pc;
              return (
                <button
                  key={pc}
                  onClick={() => { setMatMode("calculated"); setPercent(pc); }}
                  style={{
                    border: `1px solid ${on ? "var(--theme-accent)" : "var(--theme-tint-25)"}`,
                    background: on ? "var(--theme-accent)" : "#fff",
                    color: on ? "#fff" : "var(--theme-ink)",
                    borderRadius: 8, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {pc}%
                </button>
              );
            })}
            <input
              type="number" step="0.1" min="0" value={percent}
              onChange={(e) => { setMatMode("calculated"); setPercent(Number(e.target.value)); }}
              style={{ width: 80, padding: "6px 8px", border: "1px solid var(--theme-tint-25)", borderRadius: 8, fontSize: 12.5 }}
            />
            <span style={{ fontSize: 12, color: "#6b7280" }}>or any figure</span>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
            <label style={{ fontSize: 12.5, color: "var(--theme-ink)", display: "flex", gap: 6, alignItems: "center" }}>
              <input
                type="radio" name="v2matbase"
                checked={matMode === "manual"}
                onChange={() => setMatMode("manual")}
              />
              Enter an amount instead
            </label>
            <input
              type="text" placeholder="250,000" value={manualAmount}
              onChange={(e) => { setMatMode("manual"); setManualAmount(e.target.value); }}
              style={{ padding: "6px 8px", border: "1px solid var(--theme-tint-25)", borderRadius: 8, fontSize: 12.5 }}
            />
          </div>

          <div style={{
            marginTop: 10,
            background: materialityReady ? "var(--theme-tint-15)" : "#fffbeb",
            border: `1px solid ${materialityReady ? "var(--theme-accent)" : "#f5d97e"}`,
            borderRadius: 8, padding: "10px 12px",
          }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: materialityReady ? "var(--theme-ink)" : "#8a5a00" }}>
              {materialityReady ? money(computed) : "No materiality set"}
            </div>
            <div style={{ fontSize: 12, color: "var(--theme-ink-soft)", marginTop: 2 }}>
              {matMode === "manual"
                ? "Entered manually"
                : chosenBase?.amount
                  ? `${percent}% of ${chosenBase.label.toLowerCase()} of ${money(chosenBase.amount)}`
                  : "Pick a base that is reported on this return, or enter an amount"}
            </div>
          </div>

          {/* ── the rest of what the returns cannot tell us ──────────── */}
          {engagementFields.length > 0 && (
            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--theme-tint-18)" }}>
              <div style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 13, marginBottom: 4 }}>
                The rest of the engagement
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                None of this is on a tax return, so it has to come from you. Each field says where
                its default came from and which rules it unlocks — a rule cannot be evaluated until
                everything it tests is known.
              </div>

              {[...new Set(engagementFields.map((f) => f.section))].map((section) => (
                <div key={section} style={{ marginBottom: 12 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase",
                    letterSpacing: 0.4, marginBottom: 5,
                  }}>
                    {section}
                  </div>
                  {engagementFields.filter((f) => f.section === section).map((f) => (
                    <div key={f.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: "#333" }}>
                          {f.label}
                          {f.required && <span style={{ color: "#a33" }}> *</span>}
                          <span
                            title={
                              f.provenance === "return"
                                ? "Read off the returns just transcribed"
                                : f.provenance === "rulebook"
                                  ? "The value the tax team put in the rule book"
                                  : "No defensible default — please enter it"
                            }
                            style={{
                              marginLeft: 7, fontSize: 9.5, fontWeight: 700, padding: "1px 5px",
                              borderRadius: 999, textTransform: "uppercase",
                              color:
                                f.provenance === "return" ? "var(--theme-ink)"
                                  : f.provenance === "rulebook" ? "#6b7280" : "#8a5a00",
                              background:
                                f.provenance === "return" ? "var(--theme-tint-15)"
                                  : f.provenance === "rulebook" ? "#f3f4f6" : "#fffbeb",
                            }}
                          >
                            {f.provenance === "return"
                              ? "from the return"
                              : f.provenance === "rulebook" ? "rule book default" : "you set this"}
                          </span>
                        </div>
                        <div
                          title={f.impact}
                          style={{ fontSize: 11, color: "#6b7280", marginTop: 1, cursor: "help" }}
                        >
                          {f.why}
                          {f.unlocks.length > 0 && (
                            <span style={{ color: "var(--theme-ink)" }}>
                              {" "}Unlocks {f.unlocks.length} rule{f.unlocks.length === 1 ? "" : "s"}.
                            </span>
                          )}
                        </div>
                      </div>
                      {f.kind === "choice" ? (
                        <select
                          title={f.impact}
                          value={engagement[f.id] ?? f.value}
                          onChange={(e) => setEngagement((prev) => ({ ...prev, [f.id]: e.target.value }))}
                          style={{ width: 172, padding: "5px 7px", border: "1px solid var(--theme-tint-25)", borderRadius: 8, fontSize: 12.5 }}
                        >
                          {(f.choices ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                      ) : (
                        <input
                          title={f.impact}
                          type={f.kind === "text" ? "text" : "number"}
                          step={f.kind === "percent" ? "0.01" : f.kind === "money" ? "1000" : "1"}
                          value={engagement[f.id] ?? f.value}
                          onChange={(e) => setEngagement((prev) => ({ ...prev, [f.id]: e.target.value }))}
                          style={{ width: 172, padding: "5px 7px", border: "1px solid var(--theme-tint-25)", borderRadius: 8, fontSize: 12.5 }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <button
              style={{ ...primary, opacity: !materialityReady || !engagementReady || qBusy ? 0.55 : 1 }}
              disabled={!materialityReady || !engagementReady || qBusy}
              onClick={generateQuestions}
            >
              {qBusy ? "Generating…" : "Generate the questions"}
            </button>
            {!engagementReady && (
              <span style={{ marginLeft: 10, fontSize: 12, color: "#8a5a00" }}>
                Fill the required engagement fields first.
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── questions, and everything that could not be asked ───────── */}
      {qJobId && (
        <div style={{ ...card, marginTop: 12 }}>
          <div style={sectionTitle}>
            Questions
            {qStatus?.status === "running" && " — running"}
            {qStatus?.status === "error" && " — failed"}
          </div>

          {qStatus?.status === "running" && (
            <div style={{ fontSize: 12.5, color: "#6b7280" }}>{qStatus.progress ?? "Queued…"}</div>
          )}
          {qStatus?.status === "error" && (
            <div style={{ fontSize: 12.5, color: "#a33" }}>{qStatus.error}</div>
          )}

          {qStatus?.status === "done" && qJobId && (
            <div style={{
              display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 14,
              background: "var(--theme-tint-15)", border: "1px solid var(--theme-accent)", borderRadius: 8, padding: "10px 12px",
            }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 600, color: "var(--theme-ink)", fontSize: 13 }}>
                  {(qStatus.summary as { downloadName?: string } | undefined)?.downloadName ?? "Deal book with questions"}
                </div>
                <div style={{ fontSize: 12, color: "var(--theme-ink-soft)", marginTop: 2 }}>
                  The figures read from the returns, plus the Questions sheets — Run, Ask, Cleared
                  and Cannot Ask.
                </div>
              </div>
              <button
                onClick={() => downloadJobFile(qJobId, "deal-book.xlsx")}
                style={primary}
              >
                Download the deal book
              </button>
            </div>
          )}

          {((qStatus?.summary?.years ?? []) as QuestionYear[]).map((y) => (
            <div key={`${y.taxYear}-${y.form}`} style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 600, color: "var(--theme-ink)", fontSize: 13 }}>
                {y.entityName} — {y.taxYear} Form {y.form}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                Read from {y.formsRead.join(", ")}. {y.materialityExplanation}
              </div>

              {y.asked.length > 0 ? (
                <ol style={{ margin: "10px 0 0", paddingLeft: 20, fontSize: 12.5 }}>
                  {y.asked.map((q) => (
                    <li key={q.id} style={{ marginBottom: 8 }}>
                      <div style={{ color: "#222" }}>{q.text}</div>
                      {q.observedFact && (
                        <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 2 }}>{q.observedFact}</div>
                      )}
                      <div style={{ fontSize: 10.5, color: "#9aa0a6", marginTop: 1 }}>
                        {q.ruleId} · {q.id}
                        {q.priority ? ` · ${q.priority}` : ""}
                      </div>
                      {q.askedByDefault && (
                        <div
                          title={q.askedByDefault}
                          style={{
                            marginTop: 3, fontSize: 10.5, color: "#8a5a00",
                            background: "#fffbeb", border: "1px solid #f5d97e",
                            borderRadius: 6, padding: "3px 7px", display: "inline-block",
                          }}
                        >
                          Asked by default — its condition was not checked on this return
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              ) : (
                <div style={{ marginTop: 8, fontSize: 12.5, color: "#8a5a00" }}>
                  No questions for this year. Every rule that applies is listed below with the
                  reason it did not ask.
                </div>
              )}

              {/* ── cannot ask, grouped by who can unblock it ────────── */}
              {(y.cannotAsk ?? []).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--theme-ink)", marginBottom: 6 }}>
                    Not asked — {(y.cannotAsk ?? []).reduce((n, g) => n + g.items.length, 0)} questions
                  </div>
                  {(y.cannotAsk ?? []).map((g) => (
                    <details key={g.kind} style={{ marginBottom: 5 }}>
                      <summary style={{ cursor: "pointer", fontSize: 12, color: "#4b5563" }}>
                        <span
                          style={{
                            fontSize: 9.5, fontWeight: 700, padding: "1px 6px", borderRadius: 999,
                            textTransform: "uppercase", marginRight: 8,
                            color:
                              g.kind === "needs_input" ? "#8a5a00"
                                : g.kind === "needs_document" ? "#1f4e79"
                                  : g.kind === "not_built" ? "#6b7280" : "var(--theme-ink)",
                            background:
                              g.kind === "needs_input" ? "#fffbeb"
                                : g.kind === "needs_document" ? "#e8f0fa"
                                  : g.kind === "not_built" ? "#f3f4f6" : "var(--theme-tint-15)",
                          }}
                        >
                          {g.label}
                        </span>
                        {g.items.length} question{g.items.length === 1 ? "" : "s"}
                        <span style={{ color: "#9aa0a6" }}>
                          {" "}· {g.owner === "nobody" ? "no action needed" : `${g.owner} can unblock`}
                        </span>
                      </summary>
                      <ul style={{ margin: "5px 0 8px", paddingLeft: 20, fontSize: 11.5, color: "#4b5563" }}>
                        {g.items.map((c) => (
                          <li key={c.questionId} style={{ marginBottom: 4 }}>
                            {c.reason}
                            <span style={{ color: "#9aa0a6" }}> ({c.ruleId} · {c.questionId})</span>
                            {c.needs.length > 0 && (
                              <div style={{ color: "#8a5a00" }}>Needs: {c.needs.join("; ")}</div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ))}
                </div>
              )}

              {/*
                Not asked, and why. Three quite different reasons, kept apart:
                cleared is a result, needs-docs is a request the reviewer can act
                on today, and not-written is a gap in this tool rather than
                anything about the return.
              */}
              {y.notAsked.length > 0 && (
                <details style={{ marginTop: 10 }}>
                  <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--theme-ink)", fontWeight: 600 }}>
                    Not asked — {y.counts.cleared} cleared, {y.counts.blocked_data} waiting on a
                    document, {y.counts.not_implemented} not yet written
                  </summary>
                  <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 12 }}>
                    <tbody>
                      {y.notAsked.map((n) => (
                        <tr key={n.ruleId} style={{ borderTop: "1px solid var(--theme-tint-13)" }}>
                          <td style={{ padding: "6px 8px", verticalAlign: "top", whiteSpace: "nowrap" }}>
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 999,
                              color: n.outcome === "cleared" ? "var(--theme-ink)"
                                : n.outcome === "blocked_data" ? "#8a5a00" : "#6b7280",
                              background: n.outcome === "cleared" ? "var(--theme-tint-15)"
                                : n.outcome === "blocked_data" ? "#fffbeb" : "#f3f4f6",
                            }}>
                              {n.outcome === "cleared" ? "CLEARED"
                                : n.outcome === "blocked_data" ? "NEEDS DOCS" : "NOT WRITTEN"}
                            </span>
                          </td>
                          <td style={{ padding: "6px 8px", verticalAlign: "top", color: "#333", width: 210 }}>
                            {n.name}
                            <div style={{ fontSize: 10.5, color: "#9aa0a6" }}>{n.ruleId}</div>
                          </td>
                          <td style={{ padding: "6px 8px", verticalAlign: "top", color: "#4b5563" }}>
                            {n.reason}
                            {n.needs.length > 0 && (
                              <div style={{ marginTop: 3, color: "#8a5a00" }}>
                                Needs: {n.needs.join("; ")}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          ))}

          {qStatus?.warnings?.length ? (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12, color: "#8a5a00" }}>
              {qStatus.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          ) : null}
        </div>
      )}

      {runs.length > 0 && (
        <div style={card}>
          <div style={sectionTitle}>
            3. Reconstructions — {runs.filter((r) => r.status === "done").length} of {runs.length} finished
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--theme-tint-7)", color: "var(--theme-ink)", textAlign: "left" }}>
                  <Th>Form</Th>
                  <Th>Tax year</Th>
                  <Th>Boxes read</Th>
                  <Th>State</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.jobId} style={{ borderBottom: "1px solid var(--theme-tint-14)" }}>
                    <Td>
                      <strong>{r.form}</strong>
                      <div style={{ color: "#6b7280", fontSize: 11 }}>{r.file}</div>
                    </Td>
                    <Td>{r.taxYear}</Td>
                    <Td>{r.summary ? `${r.summary.boxesFilled} of ${r.summary.boxesRead}` : "—"}</Td>
                    <Td>
                      {r.status === "running" && (
                        <span style={{ color: "var(--theme-ink)" }}>{r.progress ?? "Working…"}</span>
                      )}
                      {r.status === "done" && <span style={{ color: "var(--theme-ink)", fontWeight: 600 }}>done</span>}
                      {r.status === "error" && <span style={{ color: "#b3261e" }}>{r.error}</span>}
                      {r.warnings && r.warnings.length > 0 && (
                        <ul style={{ margin: "4px 0 0", paddingLeft: 16, color: "#8a5a00", fontSize: 11 }}>
                          {r.warnings.map((w, i) => (
                            <li key={i}>{w}</li>
                          ))}
                        </ul>
                      )}
                    </Td>
                    <Td>
                      {r.status === "done" && (
                        <div style={{ display: "flex", gap: 8, whiteSpace: "nowrap" }}>
                          <button
                            onClick={() => downloadJobFile(r.jobId, `reconstructed-${r.taxYear}.pdf`)}
                            style={{
                              border: "none",
                              background: "none",
                              padding: 0,
                              color: "var(--theme-ink)",
                              cursor: "pointer",
                              font: "inherit",
                              fontWeight: 600,
                            }}
                          >
                            Download
                          </button>
                          <button
                            onClick={() => showValues(r.jobId)}
                            style={{
                              border: "none",
                              background: "none",
                              padding: 0,
                              color: "var(--theme-ink)",
                              cursor: "pointer",
                              font: "inherit",
                              textDecoration: "underline",
                            }}
                          >
                            values
                          </button>
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {phase === "error" && <div style={{ marginTop: 10, color: "#b3261e" }}>{message}</div>}
          <div style={hint}>
            Forms are read at the same time, so they finish together rather than in
            order. Put each reconstruction beside the client&rsquo;s scan and compare page to
            page — the figures were read automatically, so the comparison is the check.
          </div>
        </div>
      )}


      {values.length > 0 && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={sectionTitle}>4. What was read</div>
            <label style={{ fontSize: 12, color: "#4b5563", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={onlyFilled}
                onChange={(e) => setOnlyFilled(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              only boxes with a value
            </label>
          </div>
          <div style={{ overflowX: "auto", marginTop: 8 }}>
            <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--theme-tint-7)", color: "var(--theme-ink)", textAlign: "left" }}>
                  <Th>Box ID</Th>
                  <Th>Page</Th>
                  <Th>Line</Th>
                  <Th>Printed caption</Th>
                  <Th>Value read</Th>
                </tr>
              </thead>
              <tbody>
                {shown.map((v) => (
                  <tr key={v.id} style={{ borderBottom: "1px solid var(--theme-tint-14)" }}>
                    <Td mono>{v.id}</Td>
                    <Td>{v.page ?? "—"}</Td>
                    <Td mono>{v.lineRef || "—"}</Td>
                    <Td>{v.label || "—"}</Td>
                    <Td mono>{v.value || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={hint}>
            Every value is keyed by the IRS form&rsquo;s own box identifier, so it carries its page
            and exact position without anything having to infer them.
          </div>
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 22, fontWeight: 700, color: "var(--theme-ink)" }}>{value}</div>
    <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".06em" }}>
      {label}
    </div>
  </div>
);

const Th: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <th style={{ padding: "7px 9px", fontWeight: 600, whiteSpace: "nowrap" }}>{children}</th>
);

const Td: React.FC<{ children?: React.ReactNode; mono?: boolean }> = ({ children, mono }) => (
  <td
    style={{
      padding: "6px 9px",
      color: "#1f2937",
      fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
      fontSize: mono ? 11 : undefined,
      whiteSpace: mono ? "nowrap" : undefined,
      verticalAlign: "top",
    }}
  >
    {children}
  </td>
);

export default ReconstructPanel;
